/**
 * src/api/writeErrors.js
 *
 * Three questions every write surface has to answer, in one place.
 *
 * 1. "Did the server refuse this, or did the request never reach it?"
 *    A refusal carries an httpStatus — the server read the write and said no,
 *    and re-sending it unchanged will be refused again. A transport failure
 *    carries none: the browser could not deliver the request at all (offline,
 *    DNS, TLS, a dropped connection, a function that never responded). The two
 *    demand opposite handling — a refusal must be rolled back and shown, a
 *    transport failure must be retried once connectivity returns
 *    (usePersistence.js's network-retry contract, GH #5253).
 *
 * 2. "What do we put in front of the user?"
 *    `fetch()` rejects with `TypeError: Failed to fetch`, and every dialog in
 *    this app rendered that string verbatim into its inline error slot. It is
 *    not an error message: it names a browser API, not the user's work, and it
 *    does not say the thing that actually matters — that what they just typed
 *    is not stored. GH #5242 is exactly that: the asset dialog DID render its
 *    error, and the run still recorded "no feedback of any kind", because what
 *    it rendered communicated nothing.
 *
 * 3. "What must the screen show now?"
 *    A refusal is not only something to say — it is something to undo. The
 *    server read the write and declined it, so whatever the UI put on screen in
 *    anticipation is a value the server does not hold, and every second it
 *    stays there is the product showing a customer a state that does not exist.
 *    Worse on a lifecycle transition: a chained multi-step action can have
 *    landed its earlier writes before the refusal, so the record is neither
 *    where it was nor where the user asked it to go, and two immediate reads of
 *    it disagree (GH #6600 — a two-tab draft→review race read back "In review"
 *    and then ""). `settleRefusedWrite` is the answer to that question and the
 *    ONE place it is implemented: on a refusal it re-reads the record from the
 *    server before the error is surfaced, so the screen settles on server truth
 *    rather than on the rejected value. GH #6607 is explicitly about six
 *    modules sharing this one gap, so this must never grow a per-module copy.
 *
 *    **Wired so far: the risk lifecycle only** (`RiskLifecycleControls.run`).
 *    BIA, privacy, criticality and notifications are NOT yet routed through it
 *    — their symptoms in #6607 are addressed by the collection-registry half of
 *    that issue (a 409 on their collections now merges per record instead of
 *    reverting the user's edit), which is a different mechanism. Being the one
 *    implementation is a property of this file; it is not yet a claim that the
 *    six modules are all calling it. Do not read it as one.
 *
 * STG-B-003 is the rule being served: what the user is told must be derived
 * from the write's outcome. Telling them "Failed to fetch" is not deriving
 * anything — it is passing the outcome through untranslated. Leaving the
 * rejected value on screen is not deriving anything either.
 */

/**
 * True when the write never got a response — the request failed in transport
 * rather than being refused by the server.
 *
 * Deliberately conservative: anything carrying an httpStatus is a refusal, and
 * an unrecognised error is treated as a refusal too. Mis-classifying a refusal
 * as a transport failure would queue a doomed write for retry forever; the
 * other direction merely loses an automatic retry the user can redo by hand.
 *
 * @param {unknown} err
 * @returns {boolean}
 */
export function isNetworkFailure(err) {
  if (!err || typeof err !== 'object') return false;
  if (typeof (/** @type {any} */ (err).httpStatus) === 'number') return false;
  const name = /** @type {any} */ (err).name;
  // fetch() rejects with a TypeError when it cannot deliver the request;
  // AbortError is our own request timeout (azureClient's envFetch) or a
  // navigation cutting an in-flight call off.
  if (name === 'AbortError' || name === 'TimeoutError') return true;
  // `fetch()` itself rejects with a bare TypeError on a genuine transport
  // failure, but JavaScript also uses TypeError for ordinary programming
  // errors (`x is not a function`, `cannot read properties of undefined`) —
  // those never touched the network and must not be classified as if they
  // did (GH #6257). Judge by message vocabulary instead of the name alone.
  const message = String(/** @type {any} */ (err).message || '');
  return /failed to fetch|network ?error|load failed|networkerror|timed out|err_internet/i.test(
    message
  );
}

/**
 * A notice raised before the surface that renders it exists (GH #6607).
 *
 * `WriteErrorBanner` registers its 'seasuite:write-error' listener in a
 * `useEffect`, and it lives inside the tree App.jsx renders only once
 * `stateReady && layoutReady` — so a notice raised DURING boot is dispatched
 * into a window with no listener on it and the user is told nothing. That is
 * not a corner case: the boot-time teardown-backup replay
 * (`usePersistence.js`) raises exactly this notice on the paths where an edit
 * from the previous session could not be recovered, which are the paths where
 * being told matters most.
 *
 * A fire-and-forget event is only a control if something is listening when it
 * fires. So the notice is also parked here, and the banner drains it on mount.
 * Bounded by age so a notice nobody could have acted on does not resurface
 * much later attached to a different situation; cleared as soon as a live
 * listener takes it, so a dismissed banner is never re-raised by a remount.
 */
let pendingNotice = null;
const PENDING_NOTICE_MAX_AGE_MS = 60_000;

/**
 * Take the parked notice, if one is waiting and still recent. Called by
 * `WriteErrorBanner` on mount; returns `{ message }` (message may be null for
 * the generic text) or null when there is nothing to show.
 */
export function consumePendingWriteErrorNotice(now = Date.now()) {
  const notice = pendingNotice;
  pendingNotice = null;
  if (!notice) return null;
  if (now - notice.ts > PENDING_NOTICE_MAX_AGE_MS) return null;
  return { message: notice.message };
}

/**
 * Drop the parked notice — a live listener has it, the user dismissed it, or the
 * write it was about has since landed.
 *
 * That last one matters: 'seasuite:write-recovered' clears a VISIBLE banner, but
 * a notice parked during boot has no banner to clear yet, and would surface a
 * "not saved" warning for a write that succeeded while nobody was looking. That
 * is STG-B-003's own `crossingLooksLike` — the same wrong answer in the other
 * direction — so the recovery clears the park too.
 */
export function clearPendingWriteErrorNotice() {
  pendingNotice = null;
}

/**
 * Raise the app-shell write-error banner with the server's own refusal
 * message attached, so a specific, user-appropriate explanation (e.g. an
 * EMP-B-002 refusal) reaches the person instead of the banner's generic
 * "check your connection" text (GH #6271). `message` is optional — omitting
 * it produces the same bare event every other caller of this banner already
 * fires, so this is purely additive.
 *
 * @param {string} [message]
 */
export function dispatchWriteErrorBanner(message) {
  try {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
    // Parked BEFORE the dispatch, so a listener that is already mounted clears
    // it on the same tick and only a notice nobody heard survives to be drained.
    // Inside the browser guard: outside one there is no banner to park for, and
    // a module-level value set from a non-browser caller would outlive it.
    pendingNotice = { message: message || null, ts: Date.now() };
    window.dispatchEvent(
      new CustomEvent('seasuite:write-error', message ? { detail: { message } } : undefined)
    );
  } catch {
    /* non-browser environment — no-op */
  }
}

/** True when the browser positively believes it is offline right now. */
export function isBrowserOffline() {
  try {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
  } catch {
    return false;
  }
}

/**
 * A sentence a person can act on, for the inline error slot of whatever dialog
 * triggered the write.
 *
 * Always states what happened to the user's work, never just what the browser
 * called the failure. `subject` names the thing being saved ("asset", "BIA",
 * "control") so the message reads naturally in context.
 *
 * @param {unknown} err
 * @param {string} [subject]  e.g. 'asset' — defaults to a generic phrasing
 * @param {{ willRetry?: boolean }} [opts]
 *        willRetry: the caller has queued the write for automatic retry, so
 *        the message must not imply the change is lost.
 * @returns {string}
 */
export function describeWriteError(err, subject = 'change', opts = {}) {
  const thing = subject || 'change';

  if (isNetworkFailure(err)) {
    return opts.willRetry
      ? `No connection to the server — your ${thing} is not saved yet. It will be saved automatically when the connection returns; leave this tab open.`
      : `No connection to the server — your ${thing} has not been saved. Check your connection and try again.`;
  }

  const status = /** @type {any} */ (err)?.httpStatus;
  // A field-level clash (GH #7935): the server's message names the field, what
  // the user saw, what was saved meanwhile and what they tried — say that, not
  // a generic sentence.
  const conflicts = /** @type {any} */ (err)?.conflicts;
  const serverMessage = String(/** @type {any} */ (err)?.message || '').trim();
  if (status === 409 && Array.isArray(conflicts) && conflicts.length && serverMessage) {
    return serverMessage;
  }
  if (status === 409) {
    return `Someone else changed this ${thing} while you were editing it. Your ${thing} has not been saved — reload and re-apply your change.`;
  }
  if (status === 401 || status === 403) {
    return `Your session no longer has permission to save this ${thing}. Sign in again and re-apply your change.`;
  }

  const message = String(/** @type {any} */ (err)?.message || '').trim();
  // A server-authored message is written for a person; a raw exception string
  // is not. Only pass through the former.
  if (message && !/^(typeerror|referenceerror|syntaxerror)\b/i.test(message)) {
    return `Your ${thing} has not been saved — ${message}`;
  }
  return `Your ${thing} has not been saved. Please try again.`;
}

/**
 * True when the server answered this write and declined it.
 *
 * The complement of `isNetworkFailure`: a refusal reached the server and was
 * read, so the state the client is showing is provably not the state the server
 * holds, and re-sending the same payload will be declined again. A transport
 * failure proves nothing either way — the request may never have left the
 * browser, or may have committed with only the response lost — so it must NOT
 * trigger a resync that would overwrite an edit still queued for retry.
 *
 * @param {unknown} err
 * @returns {boolean}
 */
export function isRefusedWrite(err) {
  return !!err && typeof err === 'object' && !isNetworkFailure(err);
}

/**
 * Settle the screen after a write the server refused.
 *
 * Call this from the catch of any per-record write surface, BEFORE rendering
 * the error. When the write was refused it runs `resync` — whatever re-reads
 * the affected record(s) from the server — and awaits it, so by the time the
 * user reads "not saved" the screen beside it already shows what the server
 * actually holds. A refused lifecycle transition therefore settles on a defined
 * state instead of sitting between two, which is the property GH #6600
 * observed missing.
 *
 * Deliberate non-behaviours:
 *
 * • A transport failure resyncs NOTHING. usePersistence and the per-record
 *   outboxes keep retrying such a write, and a resync would overwrite the
 *   pending edit with stale server state — silent loss dressed as recovery.
 * • A failing `resync` never replaces the original error. The refusal is what
 *   the user needs to be told; a resync that could not run makes the screen
 *   stale, not wrong, and `resynced: false` lets the caller say so if it can.
 * • Nothing here renders anything. The caller owns its own error slot; this
 *   only guarantees the value under it is server truth.
 *
 * @param {unknown} err  the error the write rejected with
 * @param {{ resync?: () => unknown | Promise<unknown> }} [opts]
 * @returns {Promise<{ refused: boolean, resynced: boolean }>}
 */
export async function settleRefusedWrite(err, { resync } = {}) {
  if (!isRefusedWrite(err)) return { refused: false, resynced: false };
  if (typeof resync !== 'function') return { refused: true, resynced: false };
  try {
    await resync();
    return { refused: true, resynced: true };
  } catch {
    // The resync itself failed. Say so rather than pretending it ran — and
    // never let it mask the refusal the caller is about to surface.
    return { refused: true, resynced: false };
  }
}
